import React, {Component} from 'react';
import CSSModules from 'react-css-modules'
import { Link as ScrollLink } from 'react-scroll'
import config from 'config'
import container from '../../helpers/container'
import {
  ContainerDivs,
  Link,
  LinkIcon,
  ScreenedCompaniesList,
  TargetDateSeriesIssueLink,
  TargetDateSeriesPartnerLogos,
  ScrollToTop,
  AutocompleteSearch
} from '../../components'
import Styles from './Companies.css'

@CSSModules(Styles, {allowMultiple:true})
export default class Companies extends Component {
  constructor(props) {
    super(props)
    this.state = {
      title: 'Publicly-traded companies',
      description: 'We use independent research from non-profit advocacy groups to identify companies within a fund portfolio as sustainability risks.',
      url: config.domain + config.routes.companies
    }
  }

  render() {
    const fffIcon = require( '../../../static/images/icons/fff-icn-clr.png' )
    const ffbIcon = require( '../../../static/images/icons/ffb-icon.svg' )
    const ffiIcon = require( '../../../static/images/icons/ffi-icon.svg' )
    const dffIcon = require( '../../../static/images/icons/dff-icn-clr.png' )
    const gefIcon = require( '../../../static/images/icons/gef-icn-clr.png' )
    const gffIcon = require( '../../../static/images/icons/gff-icn-clr.png' )
    const pffIcon = require( '../../../static/images/icons/pff-icn-clr.png' )
    const wffIcon = require( '../../../static/images/icons/wff-icn-clr.png' )
    const tffIcon = require( '../../../static/images/icons/tff-icn-clr.png' )
    //
    return (
      <div styleName='page'>
        {
          container.helmet(
            this.state.title,
            this.state.description,
            this.state.url
          )
        }
        <div styleName='anchor' id='top'/>
        <ContainerDivs>
          <h1 styleName='small-page-title'>
            {this.state.title}
          </h1>
          <div styleName='copy'>
            <p>
              To calculate sustainability ratings for retirement plans, target date funds,
              and other mutual funds, we have to know what they're invested in.
              We depend on research from investor research firms and
              non-profit advocacy groups to identify companies within a fund
              portfolio as sustainability risks.
            </p>
            <p>
              Have feedback about our screen lists? <Link
                to={config.links.contact}
                title='Contact us'
                xstrong
              >Get in touch with us<LinkIcon /></Link>
            </p>
            <p>
              This data is also available as a spreadsheet: <Link
                to={config.links.iyvSpreadsheet}
                title='Download spreadsheet'
                xstrong
              >Download spreadsheet<LinkIcon /></Link>
            </p>
          </div>
          
          <div styleName='search-bar-wrap'>
            <AutocompleteSearch table='companies'/>
          </div>

          <h2 styleName='section-heading'>By environmental/social issue</h2>

          <nav>
            {
              [
                [0, 'fossilfuels', 'Fossil fuels'],
                [1, 'cu200', 'Top carbon reserve owners: Carbon Underground 200™'],
                [1, 'coal', 'Coal industry'],
                [1, 'oilgas', 'Oil/gas industry'],
                [1, 'coalpower', 'Top coal-fired utilities: Macroclimate® 30'],
                [1, 'utilities', 'Fossil-fired utilities'],
                [0, 'fossilfinance', 'Fossil fuel finance'],
                [0, 'fossilinsurance', 'Fossil fuel insurance'],
                [0, 'deforestation', 'Deforestation'],
                [1, 'deforestationproducers', 'Deforestation-risk agricultural commodity producer/traders'],
                [1, 'deforestationfinanciers', 'Banks and lenders'],
                [1, 'deforestationconsumers', 'Major consumer brands'],
                [0, 'genderequality', 'Gender equality'],
                [0, 'civilianfirearms', 'Civilian firearms'],
                [1, 'gunmanufacturers', 'Gun manufacturers'],
                [1, 'gunretailers', 'Gun retailers'],
                [0, 'prisonindustrialcomplex', 'Prison industrial complex'],
                [1, 'prisonindustry', 'Prison industry'],
                [1, 'borderindustry', 'Border industry'],
                [1, 'highriskprisonindustrialcomplex', 'High-risk prison industrial complex'],
                [1, 'privateprisonoperators', 'Private prison operators'],
                [0, 'militaryweapons', 'Military weapons'],
                [1, 'majormilitarycontractors', 'Major military contractors'],
                [1, 'nuclearweapons', 'Nuclear weapons manufacturers/servicers'],
                [1, 'clustermunitions', 'Cluster munitions'],
                [0, 'tobacco', 'Tobacco'],
                [1, 'tobaccoproducers', 'Tobacco producer/manufacturers'],
                [1, 'entertainmentcompanies', 'Entertainment companies that promote smoking to kids'],
              ].map((e, i)=>{
                return (
                  <button
                    key={e[1]}
                    styleName='scroll-to-item'
                  >
                    <ScrollLink
                      to={e[1]}
                      spy
                      smooth
                      offset={0}
                      duration={500}
                      title={e[2]}
                    >
                      <p styleName={'level-'+e[0]}>{e[2]}</p>
                    </ScrollLink>
                  </button>
                )
              })
            }
          </nav>


          <br/>
          <br/>

          <div styleName='content-wrap'>

            { /* fossil fuels */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='fossilfuels'/>
              <div>
               <img
                 src={fffIcon}
                 styleName='icn'
                 alt='Fossil fuels'
               />
              </div>
              <h2>
                Fossil fuels
              </h2>
              <p>
                The fossil fuel grade is based on the fund’s exposure to five
                fossil fuel screen lists, tracking several thousand companies
                primarily in the energy and utility sectors. The grade is also
                impacted by exposure to companies from the <ScrollLink
                  to={'fossilfinance'}
                  spy
                  smooth
                  offset={0}
                  duration={500}
                  title={'Fossil fuel finance'}
                >
                  <span styleName='link-look'>fossil fuel finance screen</span>
                </ScrollLink>, and the <ScrollLink
                  to={'fossilinsurance'}
                  spy
                  smooth
                  offset={0}
                  duration={500}
                  title={'Fossil fuel insurance'}
                >
                  <span styleName='link-look'>fossil fuel insurance screen</span>
                </ScrollLink>.
              </p>

              <div styleName='indent'>

                <div styleName='anchor' id='cu200'/>
                <h3>
                  Top carbon reserve owners: Carbon Underground 200™
                </h3>
                <p>
                  <Link xstrong to={config.links.fficu200} title='Carbon Underground 200'>The Carbon Underground 200™</Link>, compiled and maintained by FFI Solutions, identifies the top 100 coal and the top 100 oil and gas publicly-traded reserve holders globally, ranked by the potential carbon emissions content of their reported reserves.
                </p>
                <p>
                  The Carbon Underground 200™ is a proprietary list.
                  Contact <Link
                    to={config.links.fficu200}
                    title='Carbon Underground 200 | FFI Solutions'
                    xstrong
                  >FFI Solutions</Link> for more information.
                </p>

                <div styleName='anchor' id='coal'/>
                <h3>
                  Coal industry
                </h3>
                <p>
                  The coal industry screen consists of companies designated by <Link xstrong to={config.links.morningstarFff} title='Morningstar industry classifications'>Morningstar industry classifications</Link> as Thermal Coal or Coking Coal; companies from the <Link xstrong to={config.links.gcel} title='Global Coal Exit List'>Global Coal Exit List</Link> marked as Mining or Services for Coal Industry Sector; and the top 100 coal reserve holders from the <Link xstrong to={config.links.fficu200} title='Carbon Underground 200'>Carbon Underground 200™</Link>
                </p>
                <ScreenedCompaniesList screen='is_co' />

                <div styleName='anchor' id='oilgas'/>
                <h3>
                  Oil/gas industry
                </h3>
                <p>
                  The oil/gas screen consists of companies designated by <Link xstrong to={config.links.morningstarFff} title='Morningstar industry classifications'>Morningstar industry classifications</Link> as Oil and Gas Drilling, Oil and Gas Extraction and Production, Oil and Gas Equipment and Services, Oil and Gas Integrated, Oil and Gas Midstream, and Oil and Gas Refining and Marketing; upstream and midstream companies from the <Link xstrong to={config.links.gogel} title='Global Oil/Gas Exit List'>Global Oil/Gas Exit List</Link>; and the top 100 oil/gas reserve holders from the <Link xstrong to={config.links.fficu200} title='Carbon Underground 200'>Carbon Underground 200™</Link>.
                </p>
                <ScreenedCompaniesList screen='is_og' />

                <div styleName='anchor' id='coalpower'/>
                <h3>
                  Top coal-fired utilities: Macroclimate® 30
                </h3>
                <p>
                  The <Link xstrong to={config.links.macroclimate50} title='Macroclimate 30®'>Macroclimate® 30</Link> is an exclusion list of the 30 largest public-company owners of coal-fired power plants in Developed Markets plus China and India. Macroclimate® compiled the list of 30 companies using open-source data on coal-fired power plants worldwide.
                </p>
                <ScreenedCompaniesList screen='is_f15' />

                <div styleName='anchor' id='utilities'/>
                <h3>
                  Fossil-fired utilities
                </h3>
                <p>
                  The fossil-fired utilities screen consists of companies designated by <Link xstrong to={config.links.morningstarFff} title='Morningstar industry classifications'>Morningstar industry classifications</Link> as Utilities-Independent Power Producers, Utilities-Renewable, Utilities-Diversified, Utilities-Regulated Electric, and Utilities-Regulated Gas. (Despite the name, the Utilities-Renewable industry classification includes companies that distribute gas and/or burn fossil fuel for power generation.) It also includes companies from the <Link xstrong to={config.links.gcel} title='Global Coal Exit List'>Global Coal Exit List</Link> marked as Power for Coal Industry Sector. We remove any companies in these categories that are engaged in 100% renewable operations, pure transmission, or otherwise don't distribute gas or burn fossil fuels to generate power. We also remove a small number of utilities that have fossil fuel operations but meet the criteria for inclusion on our <Link xstrong to={config.links.clean200Fff} title='Clean200'>Clean200</Link> screen - USD revenue of at least $1 billion, and over 50% of total revenues are from green sources, as measured by Corporate Knights Research.
                </p>
                <ScreenedCompaniesList screen='is_ut' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='fff' />

              <TargetDateSeriesPartnerLogos iyvCode='fff'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* fossil fuel finance */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='fossilfinance'/>
              <div>
               <img
                 src={ffbIcon}
                 styleName='icn'
                 alt='Fossil fuel finance'
               />
              </div>
              <h2>
                Fossil fuel finance
              </h2>
              <p>
                The fossil fuel finance grade is based on the fund’s exposure
                to banks that provide lending to fossil fuels.
              </p>

              <div styleName='indent'>

                <h3>
                  Banks financing fossil fuels
                </h3>
                <p>
                  The fossil fuel finance screen consists of
                  60 of the largest
                  commercial and investment banks,
                  from the <Link
                    to='https://www.bankingonclimatechaos.org/'
                    title='Banking on Climate Chaos'
                    xstrong
                  >
                    Banking on Climate Chaos
                  </Link> report.
                  The data tracks the total fossil fuel financing of these banks
                  over 2016-2023:
                  lending, and underwriting
                  debt and equity issuances.
                </p>
                <ScreenedCompaniesList screen='is_fossil_bank' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='ffb' />

              <TargetDateSeriesPartnerLogos iyvCode='ffb'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* fossil fuel insurance */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='fossilinsurance'/>
              <div>
               <img
                 src={ffiIcon}
                 styleName='icn'
                 alt='Fossil fuel insurance'
               />
              </div>
              <h2>
                Fossil fuel insurance
              </h2>
              <p>
                The fossil fuel insurance grade is based on the fund’s exposure
                to insurers that underwrite and invest in fossil fuels.
              </p>

              <div styleName='indent'>

                <h3>
                  Insurance companies underwriting fossil fuels
                </h3>
                <p>
                  The fossil fuel insurance screen consists of
                  30 of the largest primary insurers and reinsurers,
                  from the <Link
                    to='https://docs.google.com/spreadsheets/d/157kNo3-xg7qeVHi8SF21VqjXKrSjHixL9UOKFTp8PKQ/edit#gid=769793980'
                    title='Insure Our Future Scorecard'
                    xstrong
                  >
                    Insure Our Future Scorecard
                  </Link>.
                  The data rates the fossil fuel
                  insurance policies of these companies.
                </p>
                <ScreenedCompaniesList screen='is_fossil_insure' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='ffi' />

              <TargetDateSeriesPartnerLogos iyvCode='ffi'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* deforestation */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='deforestation'/>
              <div>
               <img
                 src={dffIcon}
                 styleName='icn'
                 alt='Deforestation'
               />
              </div>
              <h2>
                Deforestation
              </h2>
              <p>The deforestation grade is based on the fund’s exposure to a list of deforestation-risk
              companies identified
              in partnership with <Link xstrong to="https://foe.org/">Friends of the Earth U.S.</Link>,
              compiled from independent research as well as from existing resources within
              the civil society research and investor advocacy communities on forest-risk
              companies, including <Link xstrong to="https://forestsandfinance.org/" target="_blank"
              title="Forests &amp; Finance">Forests &amp; Finance</Link>, <Link xstrong to="https://forest500.org/" target="_blank" title="Forest500">Forest500</Link>, <Link xstrong to="https://www.cdp.net/en/forests" target="_blank" title="CDP Forests">CDP
              Forests</Link>, <Link xstrong to="https://supply-change.org/" target="_blank"
              title="Supply Change.org">Supply Change.org</Link> and <Link xstrong to="https://www.spott.org/" target="_blank" title="ZSL SPOTT">ZSL SPOTT</Link>.
              The screens include deforestation-risk agricultural commodity producer/traders
              (palm oil, paper/pulp, rubber, timber, cattle, and soy); financial companies
              involved in lending and underwriting to deforestation-risk producers and
              traders; and major consumer goods companies that source from deforestation-risk
              producers and traders.</p>
              <p>The screens are split into two tiers, with investments in producer/traders considered a higher risk.</p>

              <div styleName='indent'>

                <div styleName='anchor' id='deforestationproducers'/>
                <h3>
                  Deforestation-risk agricultural commodity producer/traders
                </h3>
                <p>
                  Companies that produce and trade palm oil, paper/pulp, rubber, timber, cattle, and soy, and have a past record or a significant risk of contributing to deforestation, land grabbing, and human rights abuses.
                </p>
                <ScreenedCompaniesList screen='is_po_producer' />

                <div styleName='anchor' id='deforestationfinanciers'/>
                <h3>
                  Banks and lenders
                </h3>
                <p>
                  Financial institutions directly and indirectly involved in underwriting and lending to deforestation-risk producers and traders. These are the publicly listed banks drawn from the
                  platform <Link
                    to='https://forestsandfinance.org/'
                    title='Forests & Finance'
                    xstrong
                  >Forests & Finance</Link>.
                </p>
                <ScreenedCompaniesList screen='is_po_financier' />

                <div styleName='anchor' id='deforestationconsumers'/>
                <h3>
                  Major consumer brands
                </h3>
                <p>
                  Consumer goods retailers that source palm oil, paper/pulp, rubber, timber, cattle, and soy from deforestation-risk producers and traders and sell it to consumers worldwide.
                </p>
                <ScreenedCompaniesList screen='is_po_consumer' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='dff' />

              <TargetDateSeriesPartnerLogos iyvCode='dff'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* gender equality: TODO */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='genderequality'/>
              <div>
               <img
                 src={gefIcon}
                 styleName='icn'
                 alt='Gender equality'
               />
              </div>
              <h2>
                Gender equality
              </h2>
              <p>The gender equality grade is based on corporate data from <Link xstrong to="https://equileap.com/">Equileap</Link> that tracks how companies perform on
                four gender equality subcategories: gender balance in leadership and workforce;
                equal compensation and work life balance; policies promoting gender equality;
                and commitment, transparency, and accountability.</p>
              <p>
                The Equileap Gender Equality Scorecard is a proprietary dataset.
                Contact <Link
                  to='https://equileap.com/'
                  title='Equileap'
                  xstrong
                >Equileap</Link> for more information.
              </p>

              <TargetDateSeriesIssueLink iyvCode='gef' />

              <TargetDateSeriesPartnerLogos iyvCode='gef'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* guns */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='civilianfirearms'/>
              <div>
               <img
                 src={gffIcon}
                 styleName='icn'
                 alt='Civilian firearms'
               />
              </div>
              <h2>
                Civilian firearms
              </h2>
              <p>The civilian firearm grade is based on the fund’s exposure to two screen lists: civilian firearm manufacturers, and retailers that sell firearms or ammunition to the civilian market.</p>
              <p>The screens are split into two tiers, with investments in civilian firearm manufacturers considered a higher risk.</p>

              <div styleName='indent'>

                <div styleName='anchor' id='gunmanufacturers'/>
                <h3>
                  Gun manufacturers
                </h3>
                <p>
                  The leading publicly-traded companies involved in manufacturing civilian firearms including handguns, rifles, assault weapons, and ammunition.
                </p>
                <ScreenedCompaniesList screen='is_gm' />

                <div styleName='anchor' id='gunretailers'/>
                <h3>
                  Gun retailers
                </h3>
                <p>
                  Large publicly-traded companies that sell firearms or ammunition to the civilian market.
                </p>
                <ScreenedCompaniesList screen='is_gr' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='gff' />

              <TargetDateSeriesPartnerLogos iyvCode='gff'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* prisons */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='prisonindustrialcomplex'/>
              <div>
               <img
                 src={pffIcon}
                 styleName='icn'
                 alt='Prison industrial complex'
               />
              </div>
              <h2>
                Prison industrial complex
              </h2>
              <p>The prison industrial complex grade is based on the fund’s
              exposure to two
              screen lists:
              prison industry companies, and border industry companies. All the companies on
              both lists are identified by the <Link xstrong to="https://investigate.afsc.org/">American
              Friends Service Committee’s Investigate project</Link>.</p>

              <p>The screens are split into two tiers, with both the prison industry and border industry screens having a subset of companies considered a higher risk. </p>

              <div styleName='indent'>
                <div styleName='anchor' id='prisonindustry'/>
                <h3>
                  Prison industry
                </h3>
                <p>
                  The prison industry screen flags companies involved in incarceration and detention facilities (including facility management, youth and family detention, private facilities internationally, private prison financing, transportation and deportations, facility surveillance and security, and prison labor), services in facilities (including communication services, health services, banking and financial services, and food, commissary, and other goods), and supervision and monitoring (including e-carceration, community corrections, and bail bonds).
                </p>
                <ScreenedCompaniesList screen='is_pp' />

                <div styleName='anchor' id='borderindustry'/>
                <h3>
                  Border industry
                </h3>
                <p>
                   The border industry screen flags companies involved in incarceration and detention facilities (including facility management, youth and family detention, private prison financing, facility surveillance and security, transportation and deportations, and prison labor), militarization of the U.S.-Mexico border (including border construction and maintenance and border monitoring and surveillance), and immigrant monitoring and surveillance (including high-tech surveillance of immigrants, e-carceration, and bail bonds).
                </p>
                <ScreenedCompaniesList screen='is_borders' />

                <div styleName='anchor' id='highriskprisonindustrialcomplex'/>
                <h3>
                  High-risk prison industrial complex
                </h3>
                <p>
                  The high-risk prison industrial complex screen flags companies from the prison industry and border industry screens that are recommended for divestment by the American Friends Service Committee state violence criteria.
                </p>
                <ScreenedCompaniesList screen='is_pff_high' />

                <div styleName='anchor' id='privateprisonoperators'/>
                <h3>
                  Private prison operators
                </h3>
                <p>
                  For-profit private contractors that have contracts with cities, counties, states, and the federal government to operate prisons, jails, and immigration jails. Any investments in these companies earn a fund an automatic F.
                </p>
                <ScreenedCompaniesList screen='is_pff_extreme' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='pff' />

              <TargetDateSeriesPartnerLogos iyvCode='pff'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* military weapons */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='militaryweapons'/>
              <div>
               <img
                 src={wffIcon}
                 styleName='icn'
                 alt='Military weapons'
               />
              </div>
              <h2>
                Military weapons
              </h2>
              <p>Our analysis checks funds for direct stock investments in military contractors. This list includes two sub-categories: manufacturers and servicers of nuclear weapons; and current and recent manufacturers of controversial weapons, such as cluster munititions, anti-personnel landmines, incendiary weapons, and depleted uranium. To identify these companies, we used data from over 20 ethical investment firms, industry news outlets, and non-profit advocacy groups, including the <Link xstrong to="https://www.sipri.org/databases/armsindustry" title="Stockholm International Peace Research Institute (SIPRI) Arms Industry Database">Stockholm International Peace Research Institute (SIPRI) Arms Industry Database</Link>, and <Link xstrong to={config.links.paxNuclearWeapons} title='Risky Returns'>peace groups PAX and ICAN</Link>.</p>
              <p>For more information on how we identified these companies, visit <Link to={config.links.wff} xstrong title='Weapon Free Funds'>Weapon Free Funds</Link>.</p>

              <div styleName='indent'>
                <div styleName='anchor' id='majormilitarycontractors'/>
                <h3>
                  Military contractors
                </h3>
                <p>
                  Companies identified as having significant involvement in arms manufacturing and/or military contracting by one or more of over 20 data sources. Each company on the list was identified by at least one ethical investment firm for exclusion for reasons of involvement with military weapons.
                </p>
                <ScreenedCompaniesList screen='is_mmc' />

                <div styleName='anchor' id='nuclearweapons'/>
                <h3>
                  Nuclear weapons manufacturers/servicers
                </h3>
                <p>
                  Companies identified as having involvement with the production of nuclear weapons.
                </p>
                <ScreenedCompaniesList screen='is_nw' />

                <div styleName='anchor' id='clustermunitions'/>
                <h3>
                  Controversial weapons
                </h3>
                <p>
                  Companies identified as having involvement with the production of controversial weapons, such as cluster munititions, anti-personnel landmines, incendiary weapons, and depleted uranium.
                </p>
                <ScreenedCompaniesList screen='is_cm' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='wff' />

              <TargetDateSeriesPartnerLogos iyvCode='wff'/>

              <ScrollToTop />

              <hr/>

            </div>


            { /* tobacco */ }
            <div styleName='companies-section-div'>
              <div styleName='anchor' id='tobacco'/>
              <div>
               <img
                 src={tffIcon}
                 styleName='icn'
                 alt='Tobacco'
               />
              </div>
              <h2>
                Tobacco
              </h2>
              <p>The tobacco grade is based on the fund’s exposure to two screen
              lists: tobacco companies that either produce tobacco or manufacture tobacco
              products identified by industry classifications; and a list of entertainment
              companies that promote tobacco to young audiences, identified by research from
              the <Link xstrong to="https://smokefreemovies.ucsf.edu/whos-accountable" target="_blank"
              title="UCSF Smoke Free Movies Database">University of California, San Francisco
              Center for Tobacco Control Research and Education Smoke Free Movies Database</Link>.</p>

              <p>The screens are split into two tiers, with investments in tobacco producer/manufacturers considered a higher risk.</p>

              <div styleName='indent'>

                <div styleName='anchor' id='tobaccoproducers'/>
                <h3>
                  Tobacco producer/manufacturers
                </h3>
                <p>
                  Identified using Morningstar industry categories, these companies either produce tobacco or manufacture tobacco products.
                </p>
                <ScreenedCompaniesList screen='is_to' />

                <div styleName='anchor' id='entertainmentcompanies'/>
                <h3>
                  Entertainment companies that promote smoking to kids
                </h3>
                <p>
                  There is scientific consensus that exposure to tobacco in movies causes kids to begin smoking. These entertainment companies promote smoking by producing kid-rated movies that depict smoking.
                </p>
                <ScreenedCompaniesList screen='is_tm' />

              </div>

              <TargetDateSeriesIssueLink iyvCode='tff' />

              <TargetDateSeriesPartnerLogos iyvCode='tff'/>

              <ScrollToTop />

            </div>

          </div>
        </ContainerDivs>

        <br/>
        <br/>
        <br/>

      </div>
    )
  }
}